/**
 * @fileoverview Hooks for session validation and authentication requirements
 * 
 * @purpose Provides React hooks for checking session validity and handling
 *          invalid sessions gracefully. Includes automatic redirects for invalid sessions.
 * 
 * @usage Import in client components:
 *        import { useSessionValidation, useRequireAuth } from '@korvo/core/hooks/useSessionValidation'
 * 
 * @ownership Core infrastructure - authentication hooks
 */

'use client'

import { useRouter } from 'next/navigation'
import { useEffect } from 'react'

import { useSession } from '../../lib/better-auth/client'
import { isSessionValid } from '../../lib/session-validation'

export function useSessionValidation() {
  const { data: session, isPending } = useSession()
  const router = useRouter()

  useEffect(() => {
    // Only check if session is loaded
    if (isPending) return

    // If we have a session but it's invalid, redirect to signin
    if (session && !isSessionValid(session)) {
      console.log('Invalid session detected, redirecting to signin')
      router.push('/auth/signin?reason=invalid_session')
    }
  }, [session, isPending, router])

  return {
    session: isSessionValid(session) ? session : null,
    isValid: isSessionValid(session),
    isLoading: isPending
  }
}

/**
 * Hook for components that require authentication
 * Automatically redirects if session is invalid
 */
export function useRequireAuth() {
  const { session, isValid, isLoading } = useSessionValidation()
  const router = useRouter()

  useEffect(() => {
    if (!isLoading && !isValid) {
      router.push('/auth/signin?reason=authentication_required')
    }
  }, [isValid, isLoading, router])

  return {
    session,
    isValid,
    isLoading
  }
}
